import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PageHeader from "@/components/PageHeader";
import RichTextEditor from "@/components/RichTextEditor";
import { Users, Building2, Ticket, Megaphone, Search, Plus, Trash2, ShieldCheck, Eye } from "lucide-react";

const PLAN_COLOR = {
    free: "bg-sand-200 text-muted-foreground",
    pro: "bg-secondary/10 text-secondary-dark",
    team: "bg-ocean-700 text-white",
};

// ---------- USERS ----------
function UsersTab() {
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    const load = async () => {
        try {
            const { data } = await api.get(`/superadmin/users?q=${encodeURIComponent(q)}&limit=100`);
            setItems(data.items || []);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, []);
    return (
        <div className="space-y-4">
            <div className="flex gap-2">
                <div className="relative flex-1 max-w-md">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search email or name…" className="pl-9 rounded-xl" data-testid="sa-users-search" />
                </div>
                <Button onClick={load} variant="outline" className="rounded-xl" data-testid="sa-users-search-btn">Search</Button>
            </div>
            <div className="rounded-3xl bg-white border border-sand-200 overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-sand-100 text-xs uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="text-left px-5 py-3">Email</th>
                            <th className="text-left px-5 py-3">Name</th>
                            <th className="text-left px-5 py-3">Orgs</th>
                            <th className="text-left px-5 py-3">Joined</th>
                            <th className="text-left px-5 py-3">Verified</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-sand-200">
                        {items.map((u) => (
                            <tr key={u.id} className="hover:bg-sand-50/50" data-testid={`sa-user-${u.id}`}>
                                <td className="px-5 py-3 font-medium text-ocean-700">{u.email}</td>
                                <td className="px-5 py-3">{u.name || "—"}</td>
                                <td className="px-5 py-3">{u.org_count}</td>
                                <td className="px-5 py-3 text-muted-foreground">{fmtDate(u.created_at)}</td>
                                <td className="px-5 py-3">{u.email_verified ? <Badge className="bg-emerald-50 text-emerald-700 border-0">Verified</Badge> : <Badge className="bg-amber-50 text-amber-700 border-0">Pending</Badge>}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {items.length === 0 && <div className="p-8 text-center text-muted-foreground text-sm">No users match.</div>}
            </div>
        </div>
    );
}

// ---------- ORGS ----------
function OrgsTab() {
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [newPlan, setNewPlan] = useState("pro");
    const [reason, setReason] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get(`/superadmin/orgs?q=${encodeURIComponent(q)}&limit=100`);
            setItems(data.items || []);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const savePlan = async () => {
        try {
            await api.post(`/superadmin/orgs/${editing.id}/plan`, { plan: newPlan, reason });
            toast.success(`${editing.name} moved to ${newPlan.toUpperCase()}`);
            setEditing(null); setReason("");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="space-y-4">
            <div className="flex gap-2">
                <div className="relative flex-1 max-w-md">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search org name…" className="pl-9 rounded-xl" data-testid="sa-orgs-search" />
                </div>
                <Button onClick={load} variant="outline" className="rounded-xl">Search</Button>
            </div>
            <div className="rounded-3xl bg-white border border-sand-200 overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-sand-100 text-xs uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="text-left px-5 py-3">Org name</th>
                            <th className="text-left px-5 py-3">Owner</th>
                            <th className="text-left px-5 py-3">Plan</th>
                            <th className="text-left px-5 py-3">Members</th>
                            <th className="text-left px-5 py-3">Created</th>
                            <th className="text-right px-5 py-3">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-sand-200">
                        {items.map((o) => (
                            <tr key={o.id} className="hover:bg-sand-50/50" data-testid={`sa-org-${o.id}`}>
                                <td className="px-5 py-3 font-medium text-ocean-700">{o.name}</td>
                                <td className="px-5 py-3 text-muted-foreground">{o.owner_email || "—"}</td>
                                <td className="px-5 py-3"><Badge className={`border-0 ${PLAN_COLOR[o.plan] || ""}`}>{(o.plan || "free").toUpperCase()}</Badge></td>
                                <td className="px-5 py-3">{o.member_count}</td>
                                <td className="px-5 py-3 text-muted-foreground">{fmtDate(o.created_at)}</td>
                                <td className="px-5 py-3 text-right">
                                    <Button size="sm" variant="outline" className="rounded-lg" data-testid={`sa-change-plan-${o.id}`} onClick={() => { setEditing(o); setNewPlan(o.plan === "team" ? "pro" : "pro"); }}>Change plan</Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {items.length === 0 && <div className="p-8 text-center text-muted-foreground text-sm">No orgs match.</div>}
            </div>

            <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
                <DialogContent className="sm:max-w-md" data-testid="sa-plan-dialog">
                    <DialogHeader><DialogTitle className="font-heading text-2xl">Change plan · {editing?.name}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Current plan</Label>
                            <div className="mt-1"><Badge className={`border-0 ${PLAN_COLOR[editing?.plan] || ""}`}>{(editing?.plan || "free").toUpperCase()}</Badge></div>
                        </div>
                        <div>
                            <Label>New plan</Label>
                            <Select value={newPlan} onValueChange={setNewPlan}>
                                <SelectTrigger className="mt-1.5 rounded-xl" data-testid="sa-new-plan"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="free">Free</SelectItem>
                                    <SelectItem value="pro">Pro</SelectItem>
                                    <SelectItem value="team">Team</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Reason (audit trail)</Label>
                            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Complimentary Pro during onboarding" className="mt-1.5 rounded-xl" data-testid="sa-plan-reason" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditing(null)} className="rounded-xl">Cancel</Button>
                        <Button className="bg-ocean-700 text-white rounded-xl" onClick={savePlan} data-testid="sa-plan-save">Apply</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ---------- COUPONS ----------
function CouponsTab() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const blank = { code: "", type: "plan_grant", plan: "pro", duration_days: 60, discount_percent: 20, max_redemptions: 100, expires_at: "", note: "" };
    const [form, setForm] = useState(blank);

    const load = async () => {
        try {
            const { data } = await api.get("/superadmin/coupons");
            setItems(data.items || []);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const create = async () => {
        try {
            const payload = { ...form };
            if (!payload.expires_at) delete payload.expires_at;
            if (payload.type === "plan_grant") { delete payload.discount_percent; }
            else { delete payload.plan; delete payload.duration_days; }
            await api.post("/superadmin/coupons", payload);
            toast.success(`Coupon ${form.code.toUpperCase()} created`);
            setOpen(false); setForm(blank); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const del = async (id, code) => {
        if (!window.confirm(`Delete coupon ${code}?`)) return;
        try { await api.delete(`/superadmin/coupons/${id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-end">
                <Button onClick={() => setOpen(true)} className="bg-ocean-700 text-white rounded-xl" data-testid="sa-new-coupon"><Plus className="h-4 w-4 mr-1.5" /> New coupon</Button>
            </div>
            <div className="rounded-3xl bg-white border border-sand-200 overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-sand-100 text-xs uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="text-left px-5 py-3">Code</th>
                            <th className="text-left px-5 py-3">Type</th>
                            <th className="text-left px-5 py-3">Details</th>
                            <th className="text-left px-5 py-3">Redemptions</th>
                            <th className="text-left px-5 py-3">Expires</th>
                            <th className="text-right px-5 py-3"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-sand-200">
                        {items.map((c) => (
                            <tr key={c.id} className="hover:bg-sand-50/50">
                                <td className="px-5 py-3 font-mono text-ocean-700">{c.code}</td>
                                <td className="px-5 py-3"><Badge className="bg-secondary/10 text-secondary-dark border-0">{c.type === "plan_grant" ? "PLAN GRANT" : "PAYPAL DISCOUNT"}</Badge></td>
                                <td className="px-5 py-3 text-muted-foreground">{c.type === "plan_grant" ? `${(c.plan || "").toUpperCase()} · ${c.duration_days}d` : `${c.discount_percent}% off next PayPal renewal`}</td>
                                <td className="px-5 py-3">{c.redemptions_count} / {c.max_redemptions}</td>
                                <td className="px-5 py-3 text-muted-foreground">{c.expires_at || "—"}</td>
                                <td className="px-5 py-3 text-right"><Button size="sm" variant="ghost" onClick={() => del(c.id, c.code)} data-testid={`sa-coupon-delete-${c.id}`}><Trash2 className="h-4 w-4 text-coral" /></Button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {items.length === 0 && <div className="p-8 text-center text-muted-foreground text-sm">No coupons yet.</div>}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-lg" data-testid="sa-coupon-dialog">
                    <DialogHeader><DialogTitle className="font-heading text-2xl">New coupon</DialogTitle></DialogHeader>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                            <Label>Code</Label>
                            <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="e.g. LAUNCH60" className="mt-1.5 rounded-xl font-mono" data-testid="sa-coupon-code" />
                        </div>
                        <div className="col-span-2">
                            <Label>Type</Label>
                            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                                <SelectTrigger className="mt-1.5 rounded-xl" data-testid="sa-coupon-type"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="plan_grant">Plan grant (free plan for N days)</SelectItem>
                                    <SelectItem value="paypal_discount">PayPal discount (% off next renewal)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {form.type === "plan_grant" ? (
                            <>
                                <div>
                                    <Label>Plan</Label>
                                    <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                                        <SelectTrigger className="mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
                                        <SelectContent><SelectItem value="pro">Pro</SelectItem><SelectItem value="team">Team</SelectItem></SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Duration (days)</Label>
                                    <Input type="number" min="1" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: Number(e.target.value) })} className="mt-1.5 rounded-xl" />
                                </div>
                            </>
                        ) : (
                            <div className="col-span-2">
                                <Label>Discount percent</Label>
                                <Input type="number" min="1" max="100" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: Number(e.target.value) })} className="mt-1.5 rounded-xl" />
                                <div className="text-xs text-muted-foreground mt-1">Stored on the redeeming org; applied at their next PayPal subscription setup.</div>
                            </div>
                        )}
                        <div>
                            <Label>Max redemptions</Label>
                            <Input type="number" min="1" value={form.max_redemptions} onChange={(e) => setForm({ ...form, max_redemptions: Number(e.target.value) })} className="mt-1.5 rounded-xl" />
                        </div>
                        <div>
                            <Label>Expires (optional)</Label>
                            <Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="mt-1.5 rounded-xl" />
                        </div>
                        <div className="col-span-2">
                            <Label>Internal note</Label>
                            <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. Trade-show handout" className="mt-1.5 rounded-xl" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl">Cancel</Button>
                        <Button onClick={create} className="bg-ocean-700 text-white rounded-xl" data-testid="sa-coupon-create">Create</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ---------- BROADCASTS ----------
function BroadcastsTab() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const blank = { title: "", body_html: "", channel: "in_app", dismissible: true, expires_at: "", email_subject: "" };
    const [form, setForm] = useState(blank);
    const [recipients, setRecipients] = useState(null);
    const [sending, setSending] = useState(false);

    // Recompute the recipient count whenever the composer opens or the
    // channel changes — we surface it right next to the Send button so
    // Collin never accidentally emails the entire user base without
    // knowing how many people that is.
    useEffect(() => {
        if (!open) { setRecipients(null); return; }
        let cancelled = false;
        api.get(`/superadmin/broadcasts/recipient-count?channel=${form.channel}`)
            .then(({ data }) => { if (!cancelled) setRecipients(data); })
            .catch(() => { if (!cancelled) setRecipients(null); });
        return () => { cancelled = true; };
    }, [open, form.channel]);

    const load = async () => {
        try {
            const { data } = await api.get("/superadmin/broadcasts");
            setItems(data.items || []);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, []);

    // Preview-as-recipient state — fetches the fully-composed HTML from the
    // backend so the footer here matches the one recipients actually get.
    const [previewOpen, setPreviewOpen] = useState(false);
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const openPreview = async () => {
        if (!form.title.trim() || !form.body_html.trim()) {
            toast.error("Add a title and message first"); return;
        }
        setPreviewLoading(true);
        try {
            const { data } = await api.post("/superadmin/broadcasts/preview", {
                title: form.title,
                body_html: form.body_html,
                email_subject: form.email_subject || undefined,
            });
            setPreview(data);
            setPreviewOpen(true);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setPreviewLoading(false); }
    };

    const send = async () => {
        if (!form.title.trim() || !form.body_html.trim()) { toast.error("Title and message are required"); return; }
        // Never let an email broadcast slip through without confirmation —
        // if the recipient count is unknown (network hiccup, endpoint down),
        // force a stronger confirm so we don't email everyone blind.
        if (form.channel === "email" || form.channel === "both") {
            if (recipients == null) {
                if (!window.confirm("We couldn't calculate the recipient count. Send anyway?")) return;
            } else if ((recipients.will_reach ?? 0) > 0) {
                const n = recipients.will_reach;
                if (!window.confirm(`This will email ${n} verified user${n === 1 ? "" : "s"} right now. Continue?`)) return;
            }
        }
        setSending(true);
        try {
            const payload = { ...form };
            if (!payload.expires_at) delete payload.expires_at;
            if (!payload.email_subject) delete payload.email_subject;
            await api.post("/superadmin/broadcasts", payload);
            toast.success(payload.channel === "in_app" ? "Broadcast published" : "Broadcast queued — emails sending in background");
            setOpen(false); setForm(blank); load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSending(false); }
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-end">
                <Button onClick={() => setOpen(true)} className="bg-ocean-700 text-white rounded-xl" data-testid="sa-new-broadcast"><Plus className="h-4 w-4 mr-1.5" /> New broadcast</Button>
            </div>
            <div className="grid gap-3">
                {items.map((b) => (
                    <div key={b.id} className="rounded-3xl bg-white border border-sand-200 p-5 shadow-card" data-testid={`sa-broadcast-${b.id}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <div className="font-heading text-lg text-ocean-700">{b.title}</div>
                                    <Badge className="bg-secondary/10 text-secondary-dark border-0 text-[10px]">{b.channel.toUpperCase()}</Badge>
                                </div>
                                <div className="prose prose-sm max-w-none text-muted-foreground mt-2 line-clamp-3" dangerouslySetInnerHTML={{ __html: b.body_html }} />
                            </div>
                            <div className="text-right text-xs text-muted-foreground shrink-0">
                                <div>{fmtDate(b.created_at)}</div>
                                {(b.channel === "email" || b.channel === "both") && (
                                    <div className="mt-2">Emails: {b.email_sent_count ?? 0} sent · {b.email_failed_count ?? 0} failed</div>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
                {items.length === 0 && <div className="rounded-3xl bg-white border border-sand-200 p-8 text-center text-muted-foreground text-sm">No broadcasts yet.</div>}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="sa-broadcast-dialog">
                    <DialogHeader><DialogTitle className="font-heading text-2xl">New broadcast</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Title</Label>
                            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Scheduled maintenance — Sunday 3am" className="mt-1.5 rounded-xl" data-testid="sa-broadcast-title" />
                        </div>
                        <div>
                            <Label>Message</Label>
                            <div className="mt-1.5">
                                <RichTextEditor value={form.body_html} onChange={(html) => setForm((prev) => ({ ...prev, body_html: html }))} placeholder="Write your announcement…" testId="sa-broadcast-wysiwyg" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Channel</Label>
                                <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                                    <SelectTrigger className="mt-1.5 rounded-xl" data-testid="sa-broadcast-channel"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="in_app">In-app banner</SelectItem>
                                        <SelectItem value="email">Email (Mailgun)</SelectItem>
                                        <SelectItem value="both">Both</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Banner expires (optional)</Label>
                                <Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="mt-1.5 rounded-xl" />
                            </div>
                        </div>
                        {(form.channel === "email" || form.channel === "both") && (
                            <div>
                                <Label>Email subject (defaults to title)</Label>
                                <Input value={form.email_subject} onChange={(e) => setForm({ ...form, email_subject: e.target.value })} className="mt-1.5 rounded-xl" />
                            </div>
                        )}
                    </div>
                    <DialogFooter className="flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 mt-4">
                        <div className="text-xs text-muted-foreground text-left sm:text-left" data-testid="sa-broadcast-recipients">
                            {recipients == null ? (
                                <span className="opacity-60">Calculating audience…</span>
                            ) : form.channel === "in_app" ? (
                                <>
                                    This banner will appear for <b className="text-ocean-700">{recipients.will_reach}</b> user{recipients.will_reach === 1 ? "" : "s"} on their next visit.
                                </>
                            ) : (
                                <>
                                    Will email <b className="text-ocean-700">{recipients.will_reach}</b> verified user{recipients.will_reach === 1 ? "" : "s"}.
                                    {(recipients.excluded_unverified + recipients.excluded_unsubscribed) > 0 && (
                                        <span className="opacity-70"> Skipping {recipients.excluded_unverified} unverified{recipients.excluded_unsubscribed > 0 ? ` · ${recipients.excluded_unsubscribed} opted out` : ""}.</span>
                                    )}
                                </>
                            )}
                        </div>
                        <div className="flex gap-2 justify-end">
                            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl">Cancel</Button>
                            <Button
                                variant="outline"
                                onClick={openPreview}
                                disabled={previewLoading}
                                className="rounded-xl border-secondary/40 text-secondary-dark hover:bg-secondary/5"
                                data-testid="sa-broadcast-preview"
                            >
                                <Eye className="h-4 w-4 mr-1.5" />
                                {previewLoading ? "Loading…" : "Preview"}
                            </Button>
                            <Button onClick={send} disabled={sending} className="bg-ocean-700 text-white rounded-xl" data-testid="sa-broadcast-send">
                                {sending ? "Sending…" : "Send broadcast"}
                            </Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0" data-testid="sa-broadcast-preview-dialog">
                    <DialogHeader className="px-6 pt-6">
                        <DialogTitle className="font-heading text-2xl">Preview as recipient</DialogTitle>
                    </DialogHeader>
                    {preview ? (
                        <div className="flex-1 overflow-hidden flex flex-col">
                            {/* Email header — mimics an inbox row */}
                            <div className="mx-6 mt-3 rounded-t-xl border border-b-0 border-sand-300 bg-sand-100/60 p-4 text-sm">
                                <div className="grid grid-cols-[80px_1fr] gap-x-3 gap-y-1">
                                    <span className="text-muted-foreground">From</span>
                                    <span className="text-ocean-700 font-medium" data-testid="sa-preview-from">{preview.from_name} &lt;{preview.from_email}&gt;</span>
                                    <span className="text-muted-foreground">Subject</span>
                                    <span className="text-ocean-700 font-medium" data-testid="sa-preview-subject">{preview.subject}</span>
                                </div>
                            </div>
                            {/* Rendered email body via sandboxed iframe so styles are isolated. */}
                            <iframe
                                title="Broadcast email preview"
                                sandbox="allow-same-origin"
                                data-testid="sa-preview-frame"
                                srcDoc={`<!doctype html><html><head><meta charset='utf-8'><style>body{font-family:system-ui,-apple-system,'Segoe UI',sans-serif;color:#0B2B40;padding:24px;max-width:640px;margin:0 auto;line-height:1.55} h1,h2{color:#0B2B40} a{color:#00A3B5}</style></head><body>${preview.html}</body></html>`}
                                className="mx-6 flex-1 border border-sand-300 border-t-0 rounded-b-xl bg-white"
                                style={{ minHeight: "320px" }}
                            />
                            <div className="px-6 py-3 text-xs text-amber-700 bg-amber-50 border-t border-amber-200" data-testid="sa-preview-note">
                                <b>Heads up:</b> {preview.note}
                            </div>
                        </div>
                    ) : (
                        <div className="p-8 text-sm text-muted-foreground">Loading preview…</div>
                    )}
                    <DialogFooter className="px-6 pb-6">
                        <Button onClick={() => setPreviewOpen(false)} className="bg-ocean-700 text-white rounded-xl" data-testid="sa-preview-close">
                            Close preview
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}


export default function SuperAdmin() {
    const [tab, setTab] = useState("users");
    // Client-side gate — mirrors the backend `require_superadmin`. Renders
    // nothing (redirect) if the current user isn't in SUPERADMIN_EMAILS.
    const [access, setAccess] = useState("checking"); // "checking" | "granted" | "denied"
    useEffect(() => {
        api.get("/superadmin/whoami")
            .then(({ data }) => setAccess(data.is_superadmin ? "granted" : "denied"))
            .catch(() => setAccess("denied"));
    }, []);
    if (access === "checking") {
        return <div className="p-8 text-sm text-muted-foreground" data-testid="sa-access-checking">Checking access…</div>;
    }
    if (access === "denied") {
        return <Navigate to="/app" replace />;
    }
    return (
        <div>
            <PageHeader
                subtitle="Platform"
                title="Super admin"
                icon={<ShieldCheck className="h-5 w-5 text-secondary" />}
            />
            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="rounded-2xl" data-testid="sa-tabs">
                    <TabsTrigger value="users" data-testid="sa-tab-users"><Users className="h-4 w-4 mr-1.5" /> Users</TabsTrigger>
                    <TabsTrigger value="orgs" data-testid="sa-tab-orgs"><Building2 className="h-4 w-4 mr-1.5" /> Orgs</TabsTrigger>
                    <TabsTrigger value="coupons" data-testid="sa-tab-coupons"><Ticket className="h-4 w-4 mr-1.5" /> Coupons</TabsTrigger>
                    <TabsTrigger value="broadcasts" data-testid="sa-tab-broadcasts"><Megaphone className="h-4 w-4 mr-1.5" /> Broadcasts</TabsTrigger>
                </TabsList>
                <TabsContent value="users" className="mt-6"><UsersTab /></TabsContent>
                <TabsContent value="orgs" className="mt-6"><OrgsTab /></TabsContent>
                <TabsContent value="coupons" className="mt-6"><CouponsTab /></TabsContent>
                <TabsContent value="broadcasts" className="mt-6"><BroadcastsTab /></TabsContent>
            </Tabs>
        </div>
    );
}
